/* tmc-condition-panels.js — Home slot 04, the seven open condition panels (NW-17, issue #146).
   Markup and CSS: sections/tmc-condition-panels.liquid and snippets/tmc-condition-panel.liquid.

   OPENING. The grid (NW-16, assets/tmc-conditions.js) fires a cancelable `tmc:condition-open` on document from the
   expand button of an open card, detail = { key, title, index, consultLed, eyebrow, trigger }. The panel whose
   data-tmc-panel matches detail.key opens and the event is cancelled. With no matching panel the event is left alone.

   CLOSING. The close button, a click beside the panel (on the dimmed backdrop), or Escape. Focus goes back to detail.trigger.

   THE LAUNCHER ON TOP. "Book a consult" in a panel carries data-tmc-consult, so the consult launcher (NW-11) opens it
   with its own document listener; this file does nothing for it. While the launcher is open the panel stays open
   underneath and stands back: Escape and Tab belong to the launcher, which returns focus into the panel when it
   closes. The launcher cancels the Escape it handles, so the same key press never closes both.

   Every lookup happens when it is needed: the theme editor swaps section markup on shopify:section:load, and a node
   held in a closure would then be a detached copy.

   MOTION (NW-24, board motion-conditions-card-expand). From 768 the panel grows out of the card that opened it: the
   shell is clipped to the card's rect and opens to its own (a clip, not a scale, so its content is never squashed),
   the card's photo and title fly to the panel's photo and h2, the other cards step back (the nearest soonest), and
   the content rises in six groups. Close runs it back into the card in 360ms, and then hides the panel and puts
   focus back on the trigger. Below 768 the panel is a bottom sheet and rises from the foot of the screen. Reduced motion: a 150ms
   crossfade, done with filter: opacity() because tmc-home.css pins opacity to 1 inside every .tmc-section. Without
   element.animate (jsdom) every open and close is instant. */
(function () {
  'use strict';

  if (window.__tmcConditionPanels) return;
  window.__tmcConditionPanels = true;

  var state = { panel: null, trigger: null, closing: null };
  var motion = [];

  var wide = window.matchMedia('(min-width: 768px)');
  var still = window.matchMedia('(prefers-reduced-motion: reduce)');
  var OUT = 'cubic-bezier(.22,1,.36,1)';
  var BACK = 'cubic-bezier(.4,0,.2,1)';
  /* The board's six content groups, in stagger order (--i 0 to 5). The close button comes in with the last. */
  var GROUPS = [
    '.tmc-cp__handle, .tmc-cp__count, .tmc-cp__eyebrow',
    '.tmc-cp__cta',
    '.tmc-cp__tests',
    '.tmc-cp__strip, .tmc-cp__intro, .tmc-cp__sym, .tmc-cp__cond',
    '.tmc-cp__research, .tmc-cp__foot',
    '.tmc-cp__stats, .tmc-cp__close'
  ];

  function scrim() { return document.querySelector('[data-tmc-panel-scrim]'); }
  function panelFor(key) {
    return key ? document.querySelector('[data-tmc-panels] [data-tmc-panel="' + key + '"]') : null;
  }
  function launcherOpen() {
    var l = document.querySelector('[data-tmc-consult-panel]');
    return !!l && !l.hasAttribute('hidden');
  }

  /* Tell assistive tech the grid's expand buttons open a dialog. Runs again when either section reloads. */
  function markTriggers() {
    [].forEach.call(document.querySelectorAll('[data-tmc-condition-open]'), function (btn) {
      var panel = panelFor(btn.getAttribute('data-tmc-condition-open'));
      if (panel) {
        btn.setAttribute('aria-haspopup', 'dialog');
        btn.setAttribute('aria-controls', panel.id);
      }
    });
  }

  function canAnimate() { return typeof Element.prototype.animate === 'function'; }
  function run(el, frames, opts) { var a = el.animate(frames, opts); motion.push(a); return a; }
  function stop() { var list = motion; motion = []; list.forEach(function (a) { a.cancel(); }); }
  function each(root, sel, fn) { [].forEach.call(root.querySelectorAll(sel), fn); }
  function fade(el, from, to, ms) { return run(el, { filter: ['opacity(' + from + ')', 'opacity(' + to + ')'] }, { duration: ms, fill: 'forwards' }); }

  /* The card's photo and its band are one shape; so are the panel's photo and ramp (.tmc-cp__tile). */
  function photoRect(card) {
    var a = card.querySelector('.tmc-cond__photo');
    if (!a) return null;
    var r = a.getBoundingClientRect();
    var band = card.querySelector('.tmc-cond__band');
    var b = band ? band.getBoundingClientRect() : r;
    return { left: r.left, top: r.top, width: r.width, height: Math.max(r.bottom, b.bottom) - r.top };
  }
  function toRect(el, r) {
    var e = el.getBoundingClientRect();
    return 'translate(' + (r.left - e.left) + 'px, ' + (r.top - e.top) + 'px) scale(' + (r.width / e.width) + ', ' + (r.height / e.height) + ')';
  }
  /* The title keeps its glyph proportions: scaled by font size, left edges and line centres matched. */
  function toTitle(title, name) {
    var t = title.getBoundingClientRect();
    var n = name.getBoundingClientRect();
    var k = parseFloat(getComputedStyle(name).fontSize) / parseFloat(getComputedStyle(title).fontSize);
    return 'translate(' + (n.left - t.left) + 'px, ' + (n.top + n.height / 2 - t.top - t.height * k / 2) + 'px) scale(' + k + ')';
  }
  /* The title flies between the card's overlay title and the h2; its colour swaps from one to the other at the
     midpoint (board: "colour swaps at 50%"). `back` runs it from the h2 into the card. */
  function titleFrames(title, name, back) {
    var ink = getComputedStyle(title).color;
    var white = getComputedStyle(name).color;
    var at = toTitle(title, name);
    var frames = [
      { transform: at, transformOrigin: '0 0', color: white, offset: 0 },
      { color: white, offset: 0.5 },
      { color: ink, offset: 0.5 },
      { transform: 'none', transformOrigin: '0 0', color: ink, offset: 1 }
    ];
    if (!back) return frames;
    return [
      { transform: 'none', transformOrigin: '0 0', color: ink, offset: 0 },
      { color: ink, offset: 0.5 },
      { color: white, offset: 0.5 },
      { transform: at, transformOrigin: '0 0', color: white, offset: 1 }
    ];
  }
  function clipTo(shell, card) {
    var p = shell.getBoundingClientRect();
    var c = card.getBoundingClientRect();
    return 'inset(' + (c.top - p.top) + 'px ' + (p.right - c.right) + 'px ' + (p.bottom - c.bottom) + 'px ' + (c.left - p.left) + 'px round ' + getComputedStyle(card).borderTopLeftRadius + ')';
  }
  /* Every other area on screen, with its step-back delay: nearest third 0ms, then 30ms, then 60ms (board). */
  function siblings(card) {
    var item = card.closest('.tmc-cond__item');
    var grid = item && item.parentNode;
    if (!grid) return [];
    var c = card.getBoundingClientRect();
    var list = [].filter.call(grid.children, function (el) { return el !== item && el.getClientRects().length; }).map(function (el) {
      var r = el.getBoundingClientRect();
      return { el: el, d: Math.hypot(r.left + r.width / 2 - c.left - c.width / 2, r.top + r.height / 2 - c.top - c.height / 2) };
    }).sort(function (a, b) { return a.d - b.d; });
    list.forEach(function (s, k) { s.delay = 30 * Math.floor(k * 3 / list.length); });
    return list;
  }

  function animateOpen(panel, trigger) {
    stop();
    if (!canAnimate()) return;
    var s = scrim();
    if (still.matches) {
      fade(panel, 0, 1, 150);
      if (s) fade(s, 0, 1, 150);
      return;
    }
    var shell = panel.querySelector('.tmc-cp__panel');
    if (s) run(s, { opacity: [0, 1] }, { duration: 200, easing: 'ease-out' });
    GROUPS.forEach(function (sel, i) {
      each(panel, sel, function (el) {
        run(el, [{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'none' }], { duration: 220, delay: 240 + 30 * i, easing: OUT, fill: 'backwards' });
      });
    });
    var timing = { duration: 440, delay: 40, easing: OUT, fill: 'backwards' };

    if (!wide.matches) {
      var drop = 'translateY(' + (window.innerHeight - shell.getBoundingClientRect().top) + 'px)';
      run(shell, { transform: [drop, 'none'] }, timing);
      each(panel, '[data-tmc-panel-bar]', function (bar) { run(bar, { transform: ['translateY(100%)', 'none'] }, timing); });
      return;
    }

    var card = trigger && trigger.closest('.tmc-cond__card');
    if (!card) return;
    siblings(card).forEach(function (sib) {
      run(sib.el, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(.97)' }], { duration: 200, delay: sib.delay, easing: 'ease-out', fill: 'forwards' });
    });
    run(card, { opacity: [0, 0] }, { duration: 1, fill: 'forwards' });
    run(shell, { clipPath: [clipTo(shell, card), 'inset(0px round ' + getComputedStyle(shell).borderTopLeftRadius + ')'] }, timing);
    var tile = panel.querySelector('.tmc-cp__tile');
    var from = photoRect(card);
    if (tile && from) run(tile, [{ transform: toRect(tile, from), transformOrigin: '0 0' }, { transform: 'none', transformOrigin: '0 0' }], timing);
    var title = panel.querySelector('.tmc-cp__title');
    var name = card.querySelector('.tmc-cond__name');
    if (title && name) {
      run(title, titleFrames(title, name, false), timing);
    }
  }

  /* Returns the running exit, or null when there is nothing to animate. */
  function animateClose(panel, trigger) {
    stop();
    if (!canAnimate()) return null;
    var s = scrim();
    if (still.matches) {
      var out = [fade(panel, 1, 0, 150)];
      if (s) out.push(fade(s, 1, 0, 150));
      return out;
    }
    var shell = panel.querySelector('.tmc-cp__panel');
    var timing = { duration: 360, easing: BACK, fill: 'forwards' };
    var list = [];
    if (s) list.push(run(s, { opacity: [1, 0] }, timing));
    GROUPS.forEach(function (sel) {
      each(panel, sel, function (el) { list.push(run(el, { opacity: [1, 0] }, { duration: 120, easing: BACK, fill: 'forwards' })); });
    });

    if (!wide.matches) {
      var drop = 'translateY(' + (window.innerHeight - shell.getBoundingClientRect().top) + 'px)';
      list.push(run(shell, { transform: ['none', drop] }, timing));
      each(panel, '[data-tmc-panel-bar]', function (bar) { list.push(run(bar, { transform: ['none', 'translateY(100%)'] }, timing)); });
      return list;
    }

    var card = trigger && trigger.closest('.tmc-cond__card');
    if (!card || !card.getClientRects().length) { list.push(run(shell, { opacity: [1, 0] }, timing)); return list; }
    list.push(run(shell, { clipPath: ['inset(0px round ' + getComputedStyle(shell).borderTopLeftRadius + ')', clipTo(shell, card)] }, timing));
    var tile = panel.querySelector('.tmc-cp__tile');
    var to = photoRect(card);
    if (tile && to) list.push(run(tile, [{ transform: 'none', transformOrigin: '0 0' }, { transform: toRect(tile, to), transformOrigin: '0 0' }], timing));
    var title = panel.querySelector('.tmc-cp__title');
    var name = card.querySelector('.tmc-cond__name');
    if (title && name) {
      list.push(run(title, titleFrames(title, name, true), timing));
    }
    /* The card stays under the shell until it lands; the siblings come back from 160ms. */
    list.push(run(card, { opacity: [0, 0] }, { duration: 360 }));
    siblings(card).forEach(function (sib) {
      list.push(run(sib.el, [{ opacity: 0, transform: 'scale(.97)' }, { opacity: 1, transform: 'none' }], { duration: 200, delay: 160, easing: 'ease-out', fill: 'backwards' }));
    });
    return list;
  }

  /* A close still running ends now: the next open, or a section reload, must not wait for it. */
  function settleClose() { if (state.closing) state.closing(); }

  function open(panel, trigger) {
    settleClose();
    if (state.panel && state.panel !== panel) close(false, true);
    state.panel = panel;
    state.trigger = trigger || null;
    var s = scrim();
    if (s) s.hidden = false;
    panel.hidden = false;
    document.documentElement.classList.add('tmc-cp-open');
    var sheet = panel.querySelector('.tmc-cp__panel');
    /* Open at the top: the sheet scrolls below 768, the dialog itself from 768 (NW-59). */
    if (sheet) sheet.scrollTop = 0;
    panel.scrollTop = 0;
    panel.focus({ preventScroll: true });
    animateOpen(panel, trigger);
  }

  /* The panel stays up (and the page locked) until the exit ends; state.panel is cleared at once, so Escape, Tab and
     clicks already treat it as closed. */
  function close(refocus, instant) {
    var panel = state.panel;
    if (!panel) return;
    var back = state.trigger;
    state.panel = null;
    state.trigger = null;
    function done() {
      state.closing = null;
      stop();
      panel.hidden = true;
      var s = scrim();
      if (s) s.hidden = true;
      document.documentElement.classList.remove('tmc-cp-open');
      if (refocus !== false && back && back.focus) back.focus({ preventScroll: true });
    }
    var exit = instant ? null : animateClose(panel, back);
    if (!exit || !exit.length) { done(); return; }
    state.closing = done;
    /* Settles either way: if something else cancels one of these, the panel still closes. */
    var end = function () { if (state.closing === done) done(); };
    Promise.all(exit.map(function (a) { return a.finished; })).then(end, end);
  }

  document.addEventListener('tmc:condition-open', function (e) {
    var d = e.detail || {};
    var panel = panelFor(d.key);
    if (!panel) return;
    open(panel, d.trigger);
    e.preventDefault();
  });

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!state.panel || !target || !target.closest) return;
    var more = target.closest('[data-tmc-panel-more]');
    if (more && state.panel.contains(more)) {
      var block = more.closest('[data-expanded]');
      if (block) block.setAttribute('data-expanded', 'true');
      more.setAttribute('aria-expanded', 'true');
      /* The "+N more" chip is gone once the full list shows; keep focus in the list it opened. */
      var full = block && block.querySelector('.tmc-cp__full');
      if (full) { full.setAttribute('tabindex', '-1'); full.focus({ preventScroll: true }); }
      return;
    }
    /* The dialog covers the scrim, so a click beside the panel lands on the dialog itself. */
    if (target.closest('[data-tmc-panel-close]') || target === state.panel) {
      e.preventDefault();
      close();
    }
  });

  /* A modal keeps the tab ring inside itself. Hidden controls (the sticky bar above 768, the header button below
     it) are skipped by measuring them. */
  function trap(e) {
    var all = state.panel.querySelectorAll('a[href], button:not([disabled])');
    var items = [];
    for (var i = 0; i < all.length; i++) if (all[i].getClientRects().length) items.push(all[i]);
    if (!items.length) return;
    var head = items[0];
    var tail = items[items.length - 1];
    var live = document.activeElement;
    if (e.shiftKey && (live === head || live === state.panel)) { e.preventDefault(); tail.focus(); }
    else if (!e.shiftKey && live === tail) { e.preventDefault(); head.focus(); }
  }

  document.addEventListener('keydown', function (e) {
    /* While a panel runs its exit it is still drawn; keep focus in it until it is hidden. */
    if (state.closing && e.key === 'Tab') { e.preventDefault(); return; }
    if (!state.panel || e.defaultPrevented || launcherOpen()) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'Tab') trap(e);
  });

  document.addEventListener('shopify:section:load', function () {
    settleClose();
    if (state.panel && !document.contains(state.panel)) {
      state.panel = null;
      stop();
      document.documentElement.classList.remove('tmc-cp-open');
    }
    markTriggers();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', markTriggers);
  else markTriggers();
})();
